// Pure offline-auth primitives: PIN hashing (PBKDF2-SHA256) and the failed-attempt
// lockout state machine. Deliberately free of IndexedDB / Vue / network deps so the
// security-critical logic can be unit-tested in isolation (node --test).

const ITERATIONS = 150_000;
const HASH_BITS = 256;
export const MAX_ATTEMPTS = 5;
export const BASE_COOLDOWN_MS = 30_000;

const toB64 = (bytes) => btoa(String.fromCharCode(...bytes));
const fromB64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

/** Random 16-byte salt, base64. */
export function generateSalt() {
  return toB64(crypto.getRandomValues(new Uint8Array(16)));
}

/** PBKDF2-SHA256(pin, salt) → base64 (256-bit). The raw PIN is never persisted. */
export async function hashPin(pin, saltB64) {
  const keyMaterial = await crypto.subtle.importKey(
    'raw', new TextEncoder().encode(String(pin)), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: fromB64(saltB64), iterations: ITERATIONS, hash: 'SHA-256' },
    keyMaterial, HASH_BITS);
  return toB64(new Uint8Array(bits));
}

/** Constant-time-ish compare of a candidate PIN against a stored hash. */
export async function verifyPin(pin, saltB64, expectedHashB64) {
  const actual = await hashPin(pin, saltB64);
  return constantTimeEqual(actual, expectedHashB64);
}

function constantTimeEqual(a, b) {
  if (typeof a !== 'string' || typeof b !== 'string' || a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

/**
 * Pure lockout state machine. Given the current attempt state and whether the PIN
 * matched, returns the next { failedAttempts, lockedUntil } plus a result tag.
 * Lock kicks in at MAX_ATTEMPTS with an escalating cooldown: 30s, 60s, 120s, ...
 */
export function nextLockState({ failedAttempts = 0, lockedUntil = 0 }, success, now) {
  if (lockedUntil > now) {
    return { state: { failedAttempts, lockedUntil }, result: 'locked' };
  }
  if (success) {
    return { state: { failedAttempts: 0, lockedUntil: 0 }, result: 'ok' };
  }
  const attempts = failedAttempts + 1;
  let lock = 0;
  if (attempts >= MAX_ATTEMPTS) {
    const over = attempts - MAX_ATTEMPTS;                 // 0 at first lock
    lock = now + BASE_COOLDOWN_MS * Math.pow(2, over);    // 30s, 60s, 120s, ...
  }
  return {
    state: { failedAttempts: attempts, lockedUntil: lock },
    result: lock ? 'locked_out' : 'bad_pin',
  };
}
